import { useMemo } from 'react';

import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';

import { FetchItemsSort } from '../../../../../../../types';
import {
  FetchProductsCacheKey,
  FetchProductsFilters,
  FetchProductsSort,
  FetchProductsSortTypes
} from '../../../../../../products/productsTypes';
import { ThreeDStockContentSortDropdownButtonButtonsType } from '../../../../headers/ThreeDStockContentSortAndView/components/ThreeDStockContentSortDropdownButton';

import { useProductsSortDropdown } from '../../../../../../products/hooks/useProductsSortDropdown';

import { words } from '../../../../../../../locales/keys';

interface ProductsSortDropdownOptions {
  productsBaseCacheKey: FetchProductsCacheKey;
  productsFilters: FetchProductsFilters;
  sortProducts: (nextSort: FetchProductsSort) => void;
  productsSort: FetchItemsSort;
}

function useThreeDStockProductsContentSortButtons({
  productsBaseCacheKey,
  sortProducts,
  productsFilters,
  productsSort
}: ProductsSortDropdownOptions) {
  const {
    handleSortByMostLiked,
    handleSortByMostUsed,
    handleSortByNewest,
    handleSortByFeatured
  } = useProductsSortDropdown({
    productsBaseCacheKey,
    sortProducts
  });

  const sortBy = useMemo(() => head(productsSort), [productsSort]);

  const sortButtons = useMemo<
    ThreeDStockContentSortDropdownButtonButtonsType[]
  >(() => {
    const withFeatured = !isEmpty(productsFilters.nameSkuTags);

    const defaultButtons = [
      {
        id: 'newest',
        i18nText: words.newest,
        onSelect: handleSortByNewest,
        active: sortBy === FetchProductsSortTypes.CREATED_AT_DESC
      },
      {
        id: 'mostUsed',
        i18nText: words.mostUsed,
        onSelect: handleSortByMostUsed,
        active: sortBy === FetchProductsSortTypes.SELECTED_PRODUCTS_COUNT_DESC
      },
      {
        id: 'mostLiked',
        i18nText: words.mostLiked,
        onSelect: handleSortByMostLiked,
        active:
          sortBy === FetchProductsSortTypes.USERS_CHOSEN_PRODUCTS_COUNT_DESC
      }
    ];

    return withFeatured
      ? [
          ...defaultButtons,
          {
            id: 'featured',
            i18nText: words.featured,
            onSelect: handleSortByFeatured,
            active: sortBy === FetchProductsSortTypes.SCORE_DESC
          }
        ]
      : defaultButtons;
  }, [
    handleSortByFeatured,
    handleSortByMostLiked,
    handleSortByMostUsed,
    handleSortByNewest,
    productsFilters.nameSkuTags,
    sortBy
  ]);

  return {
    sortButtons,
    sortBy
  };
}

export default useThreeDStockProductsContentSortButtons;
